import React, { useEffect, useState } from 'react';
import { ImageEffectsProps, ProcessedImageResult } from '../types';
import { useImageProcessor } from '../hooks/useImageProcessor';

const ImageEffects: React.FC<ImageEffectsProps> = ({
    image,
    effect,
    intensity = 100,
    blurRadius,
    onProcessingComplete,
    onError,
    className,
    style,
}) => {
    const [processedImageUrl, setProcessedImageUrl] = useState<string | null>(null);
    const [effectIntensity, setEffectIntensity] = useState<number>(intensity);

    const { imageBlob, imageUrl, isLoading, applyEffect } = useImageProcessor({
        image,
        onError,
    });

    // Process the image when effect or intensity changes
    useEffect(() => {
        const processImage = async () => {
            if (!imageBlob) return;

            try {
                // Apply the selected effect
                const effectBlob = await applyEffect(effect, effectIntensity);
                const effectUrl = URL.createObjectURL(effectBlob);

                setProcessedImageUrl(effectUrl);

                if (onProcessingComplete) {
                    onProcessingComplete(effectBlob);
                }
            } catch (err) {
                console.error('Error applying effect:', err);
                if (onError && err instanceof Error) {
                    onError(err);
                }
            }
        };

        processImage();

        // Cleanup function
        return () => {
            if (processedImageUrl) {
                URL.revokeObjectURL(processedImageUrl);
            }
        };
    }, [imageBlob, effect, effectIntensity, onProcessingComplete, onError, applyEffect]);

    // Handle intensity slider change
    const handleIntensityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEffectIntensity(Number(e.target.value));
    };

    // Get effect display name
    const getEffectDisplayName = (effectName: string): string => {
        return effectName.charAt(0).toUpperCase() + effectName.slice(1);
    };

    if (isLoading) {
        return <div className={className} style={style}>Loading image...</div>;
    }

    return (
        <div
            className={className}
            style={{
                ...style,
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                {/* Original Image */}
                <div style={{ flex: 1, minWidth: '250px' }}>
                    <h3>Original</h3>
                    {imageUrl && (
                        <img
                            src={imageUrl}
                            alt="Original"
                            style={{
                                maxWidth: '100%',
                                display: 'block',
                                border: '1px solid #ddd',
                            }}
                        />
                    )}
                </div>

                {/* Processed Image with Effect */}
                <div style={{ flex: 1, minWidth: '250px' }}>
                    <h3>{getEffectDisplayName(effect)} Effect</h3>
                    {processedImageUrl ? (
                        <img
                            src={processedImageUrl}
                            alt={`${effect} effect`}
                            style={{
                                maxWidth: '100%',
                                display: 'block',
                                border: '1px solid #ddd',
                            }}
                        />
                    ) : (
                        <div style={{
                            width: '100%',
                            aspectRatio: '16/9',
                            backgroundColor: '#f0f0f0',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: '1px solid #ddd',
                        }}>
                            Applying effect...
                        </div>
                    )}
                </div>
            </div>

            {/* Effect Controls */}
            <div style={{ marginTop: '1rem' }}>
                <div style={{ marginBottom: '0.5rem' }}>
                    <label htmlFor="intensity" style={{ display: 'block', marginBottom: '0.25rem' }}>
                        Intensity: {effectIntensity}%
                    </label>
                    <input
                        type="range"
                        id="intensity"
                        min="0"
                        max="100"
                        value={effectIntensity}
                        onChange={handleIntensityChange}
                        style={{ width: '100%' }}
                    />
                </div>

                <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                    <p>
                        {effect === 'grayscale' && 'Converts the image to grayscale (black and white).'}
                        {effect === 'sepia' && 'Applies a warm brownish tone to the image, similar to vintage photographs.'}
                        {effect === 'invert' && 'Inverts all the colors in the image, creating a negative effect.'}
                        {effect === 'blur' && 'Applies a gaussian blur to the image, making it less sharp.'}
                        {effect === 'brightness' && 'Adjusts the brightness of the image.'}
                        {effect === 'contrast' && 'Adjusts the contrast between light and dark areas of the image.'}
                        {effect === 'saturation' && 'Adjusts the intensity of colors in the image.'}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ImageEffects; 